import { AlternatePayeeList, PromiseResponse } from '../../../types';
import { validateAlternatePayee } from './alternatePayeeValidator';

type ValidatorFn = (json: any) => AlternatePayeeList;

export const createResponseParser = (validator: ValidatorFn) => ({
  get: async (res: Response): Promise<PromiseResponse> => {
    try {
      const { status } = res;
      const json = await res.json();
      const normalized = {
        ...json,
        alternatePayees: json.alternatePayees?.map((payee: any) => ({
          ...payee,
          paymentCurrency: payee.paymentCurrency ?? '',
          accountType: payee.accountType ?? '',
          altairVendorId: payee.altairVendorId ?? ''
        }))
      };
      const data: AlternatePayeeList = validator(normalized);
      return { data, status, message: null };
    } catch (err) {
      const error = err as Error;
      console.warn('Error parsing GET response:', error.message);
      return {
        data: null,
        status: 0,
        message: error.message || 'Failed to parse response data'
      };
    }
  },

  persist: async (
    res: Response,
    messages: { created: string; updated: string }
  ): Promise<PromiseResponse> => {
    try {
      const { status } = res;
      const message = status === 201
        ? messages.created
        : status === 200
        ? messages.updated
        : res.statusText || 'Operation completed';

      return { data: null, status, message };
    } catch (err) {
      const error = err as Error;
      console.warn('Error parsing mutation response:', error.message);
      return {
        data: null,
        status: 0,
        message: error.message || 'Failed to process response'
      };
    }
  },

  error: async (res: Response): Promise<PromiseResponse> => {
    try {
      const { status } = res;
      const text = await res.text();

      // No response body
      if (!text || text.trim() === '') {
        return {
          data: null,
          status,
          message: res.statusText || 'An error occurred'
        };
      }

      // Try to parse JSON for structured error
      try {
        const json = JSON.parse(text);
        return {
          data: null,
          status,
          message: json.errorReason || json.errorMessage || json.message || res.statusText || 'An error occurred'
        };
      } catch {
        // Plain text error
        return { data: null, status, message: text };
      }
    } catch (err) {
      const error = err as Error;
      console.warn('Error parsing error response:', error.message);
      return {
        data: null,
        status: 0,
        message: error.message || 'Unknown error occurred'
      };
    }
  }
});

// Default instance with standard validator
export const alternatePayeeResponseParser = createResponseParser(validateAlternatePayee);
